import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconCheck } from '@calendly/ui/components/glyphs/IconCheck';
import { IconPlayerRecordFilled } from '@calendly/ui/components/glyphs/IconPlayerRecordFilled';
import { ArrowChevronRightMiniIcon } from '@calendly/ui/components/icons';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { EVENT_TYPE_COLOR_DEFINITIONS, getEventTypeColorToken } from '@calendly/ui/utils/event-type-colors';
import { useCallback, useState } from 'react';
import { useHoverOpenMenu } from '../hooks/use_hover_open_menu';
const menuRowCss = "mfiyinf";
const rowLabelCss = "r1ouqe9s";
const colorSwatchCss = "cizqm0y";

// One static class per color: Linaria's `css` tag compiles statically, so these can't be
// generated dynamically from EVENT_TYPE_COLOR_DEFINITIONS at runtime.
const colorSwatchRedCss = "c142neum";
const colorSwatchLightPinkCss = "ccnr7jq";
const colorSwatchMagentaCss = "cqbksg7";
const colorSwatchVioletCss = "c18pieuy";
const colorSwatchBlueCss = "c1bk5cnh";
const colorSwatchCyanCss = "c3var6j";
const colorSwatchLimeGreenCss = "cru37cp";
const colorSwatchElectricLimeCss = "c47b87l";
const colorSwatchBrightYellowCss = "c15xroxt";
const colorSwatchOrangeCss = "c1pwkyfa";
const COLOR_SWATCH_CSS_BY_I18N_KEY = {
  red: colorSwatchRedCss,
  light_pink: colorSwatchLightPinkCss,
  magenta: colorSwatchMagentaCss,
  violet: colorSwatchVioletCss,
  blue: colorSwatchBlueCss,
  cyan: colorSwatchCyanCss,
  lime_green: colorSwatchLimeGreenCss,
  electric_lime: colorSwatchElectricLimeCss,
  bright_yellow: colorSwatchBrightYellowCss,
  orange: colorSwatchOrangeCss
};

// Fully independent DropdownMenu (own isOpen, own trigger/content) rather than a DropdownSubmenu
// nested in the kebab's own menu: CUI's docs warn submenus aren't fully supported for
// selection-list content like this, and nesting it let the kebab's own auto-close-on-select and
// outside-click detection race the color commit and drop it. Mirrors
// ../tabs/booking_page/question_type_menu.js.
//
// `menuPortalRoot` must be the same node the kebab's own DropdownMenu uses (threaded through from
// header_more_menu.js), so both Popovers portal into one shared container - otherwise the kebab
// treats a click in this (separately-portaled) flyout as an outside click and closes before
// onSelect commits the color.
export const EventColorMenu = ({
  activeColor,
  onSelectColor,
  onColorSelected,
  menuPortalRoot
}) => {
  const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
  // See use_hover_open_menu.js for the open/close behavior.
  const {
    triggerProps,
    contentProps
  } = useHoverOpenMenu({
    isOpen: isColorMenuOpen,
    onOpen: useCallback(() => setIsColorMenuOpen(true), []),
    onClose: useCallback(() => setIsColorMenuOpen(false), [])
  });
  const handleSelectColor = hex => {
    onSelectColor(hex);
    setIsColorMenuOpen(false);
    onColorSelected?.();
  };
  return <DropdownMenu isOpen={isColorMenuOpen} onClose={() => setIsColorMenuOpen(false)} menuPortalRoot={menuPortalRoot}>
      <DropdownMenuTrigger>
        <BareButton className={menuRowCss} onClick={() => setIsColorMenuOpen(isOpen => !isOpen)} {...triggerProps} data-testid="header-more-menu-event-color-trigger">
          <span className={rowLabelCss}>
            <UiIcon icon={IconPlayerRecordFilled} size="small" color={getEventTypeColorToken(activeColor)} title="" />
            {I18n.t('publisher.event_type_editor_crud.header.kebab.event_color')}
          </span>
          {/* Manually added: CUI only renders this automatically for a real DropdownSubmenu. */}
          <ArrowChevronRightMiniIcon />
        </BareButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent placement="right top" {...contentProps}>
        {EVENT_TYPE_COLOR_DEFINITIONS.map(({
        hex,
        i18nKey
      }) => <DropdownMenuItem key={hex} onSelect={() => handleSelectColor(hex)} data-testid={`header-more-menu-color-${i18nKey}`}>
            <span className={menuRowCss}>
              <span className={rowLabelCss}>
                <span className={`${colorSwatchCss} ${COLOR_SWATCH_CSS_BY_I18N_KEY[i18nKey]}`} />
                {I18n.t(`publisher.event_types.color.${i18nKey}`)}
              </span>
              {activeColor?.toLowerCase() === hex && <UiIcon icon={IconCheck} size="small" title="" />}
            </span>
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
};

require("./event_color_menu.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./event_color_menu.js");